import React, { BaseSyntheticEvent, KeyboardEvent, useState } from 'react';
import 'antd/dist/antd'
import { Button, Spin } from 'antd'; // Import Spin
import { EditOutlined, CheckOutlined, CloseOutlined, LoadingOutlined } from '@ant-design/icons' // Import LoadingOutlined
import { useSelector } from 'react-redux';
import { RootState } from '../../../reduxStore/reducers/rootReducer';

const Comments = ({
    baseId,
    priceChangeId,
    comments,
    commentsSubmitHandler,
    stores,
    isLoading // New prop for loading state
}: {
    baseId: Number,
    priceChangeId: Number,
    comments: string,
    commentsSubmitHandler: Function,
    stores: string[],
    isLoading: boolean // Define the new prop type
}) => {

    const readOnlyState = useSelector(
        (state: RootState) => state.readOnly
    )

    const [isEditing, setIsEditing] = useState(false);
    const [isHovering, setIsHovering] = useState(false);
    const [commentsEditValue, setcommentsEditValue] = useState(comments);

    // Reset commentsEditValue when comments prop changes and not editing
    React.useEffect(() => {
        if (!isEditing) {
            setcommentsEditValue(comments);
        }
    }, [comments, isEditing]);


    const handleEditOnClick = () => {
        // Only allow editing if not currently loading
        if (!isLoading) {
            setcommentsEditValue(comments);
            setIsEditing(!isEditing);
        }
    }

    const handleCommentsChange = (e: BaseSyntheticEvent) => {
        setcommentsEditValue(e.target.value);
    }

    const handleSubmitAndClose = () => {
        commentsSubmitHandler(baseId, priceChangeId, commentsEditValue, stores);
        setIsEditing(false); // Close edit mode after submitting
    }

    const handleKeydown = (e: KeyboardEvent) => {
        if (isLoading) return; // Prevent actions while loading

        switch (e.key) {
            case 'Escape': {
                setIsEditing(false);
                setcommentsEditValue(comments); // Revert to original comments on escape
                break;
            }
            case 'Enter': {
                e.preventDefault(); // Prevent new line in textarea
                handleSubmitAndClose();
                break;
            }
        }
    }

    const cellStyle: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'relative',
        minHeight: '30px', // Ensure enough space for content/spinner
    };

    const editButtonStyle: React.CSSProperties = {
        display: readOnlyState.isReadOnly || isLoading ? 'none' : isHovering ? 'block' : 'none', // Hide edit button when loading
    }

    const inputStyle: React.CSSProperties = {
        width: '75%',
        marginRight: '5%',
    }

    const editJSX = (
        <>
            <textarea
                style={inputStyle}
                value={commentsEditValue}
                placeholder="Enter comments here"
                autoFocus
                onChange={handleCommentsChange}
                onKeyDown={handleKeydown}
                disabled={isLoading} // Disable textarea when loading
            />
            <Button title="Cancel" icon={<CloseOutlined />} onClick={handleEditOnClick} disabled={isLoading} />
            <Button title="Save" icon={<CheckOutlined />} onClick={handleSubmitAndClose} disabled={isLoading} />
        </>
    );

    const commentsJSX = (
        <>
            {comments ? (<p style={{ margin: '5px 0px' }}>{comments}</p>) : (<p style={{ height: '4px' }}></p>)}
            <div>
                <Button title="Click to edit the comments"
                    style={editButtonStyle}
                    icon={<EditOutlined />} onClick={handleEditOnClick}
                    disabled={isLoading} // Disable edit button when loading
                />
            </div>
        </>
    );


    return (
        <div style={cellStyle} onMouseEnter={() => { setIsHovering(true) }} onMouseLeave={() => { setIsHovering(false) }}>
            <Spin spinning={isLoading} indicator={<LoadingOutlined style={{ fontSize: 18 }} spin />}>
                {/* Content is wrapped in a span to apply pointerEvents: 'none' */}
                <span style={{ pointerEvents: isLoading ? 'none' : 'auto', display: 'flex', alignItems: 'center', width: '100%' }}>
                    {isEditing ? editJSX : commentsJSX}
                </span>
            </Spin>
        </div>
    );
}

export default Comments;